import ParentEvaluationInfo from 'components/modal/evaluations/components/ParentEvaluationInfo';
import {
  Col,
  Divider,
  Empty,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Typography
} from 'antd';
import { useCallback } from 'react';
import type { IProfessorAreaEvaluationsList } from 'interfaces/professor_area/evaluations';
import type { SelectProps } from 'antd';

export interface ModalCreateEvaluationStep1Props {
  assessmentSelectProps: SelectProps;
  /** When true, show Avaliação vs Atividade and the matching selector (admin). */
  allowAssignmentType?: boolean;
  assignmentSelectProps?: SelectProps;
  /** Quando true, esconde o toggle "Nova avaliação" e campos relacionados. */
  isReplacement?: boolean;
  /** Avaliação pai quando criando uma substitutiva (exibida no topo). */
  replacementParent?: IProfessorAreaEvaluationsList;
}

const ASSESSMENT_KIND_OPTIONS = [
  { label: 'Objetiva', value: 'objective' },
  { label: 'Discursiva', value: 'discursive' },
  { label: 'Mista', value: 'mixed' }
];

const Step1AssessmentOnly = ({
  assessmentSelectProps,
  assignmentSelectProps,
  isReplacement,
  replacementParent
}: Pick<
  ModalCreateEvaluationStep1Props,
  | 'assessmentSelectProps'
  | 'assignmentSelectProps'
  | 'isReplacement'
  | 'replacementParent'
>) => {
  const form = Form.useFormInstance();
  const type = Form.useWatch('type');
  const evaluationType = Form.useWatch('evaluation_type');

  const onChangeType = useCallback(
    (next: 'list' | 'new') => {
      if (next === 'new') {
        form.setFieldsValue({ evaluation_id: undefined });
      } else {
        form.setFieldsValue({
          assessment_kind: undefined,
          time_in_minutes: undefined,
          questions: undefined
        });
      }
    },
    [form]
  );

  const onChangeEvaluationType = useCallback(() => {
    form.setFieldsValue({ evaluation_id: undefined });
  }, [form]);

  const effectiveType = isReplacement ? 'list' : type;
  const showAssignmentOption = isReplacement && Boolean(assignmentSelectProps);
  const selectedEvaluationType: 'Assessment' | 'Assignment' =
    showAssignmentOption && evaluationType === 'Assignment'
      ? 'Assignment'
      : 'Assessment';

  return (
    <>
      {isReplacement && replacementParent && (
        <>
          <ParentEvaluationInfo evaluationData={replacementParent} />
          <Divider />
        </>
      )}

      <Typography.Title level={4} className="!mb-8">
        {isReplacement ? 'Selecione a substitutiva' : 'Selecione a avaliação'}
      </Typography.Title>

      <Form.Item
        label="Avaliação"
        name="type"
        initialValue="list"
        hidden={isReplacement}
        rules={[
          {
            required: true,
            message: 'Por favor, selecione o tipo de avaliação'
          }
        ]}
      >
        <Radio.Group
          options={[
            { label: 'Banco de avaliações', value: 'list' },
            { label: 'Nova avaliação', value: 'new' }
          ]}
          optionType="button"
          buttonStyle="outline"
          onChange={e => onChangeType(e.target.value)}
        />
      </Form.Item>

      {!isReplacement && <Divider />}

      {showAssignmentOption && (
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Tipo"
              name="evaluation_type"
              initialValue="Assessment"
              rules={[{ required: true, message: 'Selecione o tipo' }]}
            >
              <Select
                onChange={onChangeEvaluationType}
                options={[
                  { label: 'Avaliação', value: 'Assessment' },
                  { label: 'Atividade', value: 'Assignment' }
                ]}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

      {effectiveType === 'list' && (
        <Form.Item
          label={
            selectedEvaluationType === 'Assignment'
              ? 'Modelo de atividade'
              : 'Modelo de avaliação'
          }
          name="evaluation_id"
          rules={[
            {
              required: true,
              message: 'Por favor, selecione a avaliação'
            }
          ]}
        >
          {selectedEvaluationType === 'Assignment' && assignmentSelectProps ? (
            <Select
              {...assignmentSelectProps}
              notFoundContent={
                <Empty description="Não há atividades cadastradas para essa disciplina" />
              }
              placeholder="Selecione a atividade"
              allowClear
            />
          ) : (
            <Select
              {...assessmentSelectProps}
              notFoundContent={
                <Empty description="Não há avaliações cadastradas para essa disciplina" />
              }
              placeholder="Selecione a avaliação"
              allowClear
            />
          )}
        </Form.Item>
      )}

      <Form.Item
        label="Nome da avaliação"
        name="name"
        rules={[
          {
            required: true,
            message: 'Por favor, insira o nome da avaliação'
          },
          { min: 3, message: 'O nome deve ter pelo menos 3 caracteres' }
        ]}
      >
        <Input placeholder="Digite o nome da avaliação" />
      </Form.Item>

      {effectiveType === 'new' && (
        <Row gutter={[16, 0]}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Tipo de questões"
              name="assessment_kind"
              rules={[
                {
                  required: true,
                  message: 'Selecione o tipo de questões'
                }
              ]}
            >
              <Select
                options={ASSESSMENT_KIND_OPTIONS}
                placeholder="Selecione"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Duração (em minutos)"
              name="time_in_minutes"
              rules={[
                { required: true, message: 'Informe a duração' },
                {
                  type: 'number',
                  min: 1,
                  message: 'Informe um valor maior que zero'
                }
              ]}
            >
              <InputNumber min={1} className="!w-full" placeholder="Ex.: 60" />
            </Form.Item>
          </Col>
        </Row>
      )}
    </>
  );
};

const Step1WithAssignmentType = ({
  assessmentSelectProps,
  assignmentSelectProps
}: Pick<
  ModalCreateEvaluationStep1Props,
  'assessmentSelectProps' | 'assignmentSelectProps'
>) => {
  const form = Form.useFormInstance();
  const evaluationType = Form.useWatch('evaluation_type');

  const handleSelectType = () => {
    form.setFieldsValue({ evaluation_id: undefined });
  };

  return (
    <>
      <Typography.Title level={4} className="!mb-2">
        Informações gerais
      </Typography.Title>
      <Typography.Paragraph type="secondary" className="!mb-8 text-sm">
        Escolha se é avaliação ou atividade, depois período, tipo (padrão ou
        final), peso e demais parâmetros. Use os ícones de ajuda para média
        ponderada e pesos.
      </Typography.Paragraph>

      <Row gutter={[16, 16]}>
        <Col md={12}>
          <Form.Item
            label="Tipo"
            name="evaluation_type"
            rules={[{ required: true, message: 'Selecione o tipo' }]}
          >
            <Select placeholder="Tipo" onChange={handleSelectType}>
              <Select.Option value="Assessment">Avaliação</Select.Option>
              <Select.Option value="Assignment">Atividade</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col md={12}>
          <Form.Item
            label={evaluationType === 'Assignment' ? 'Atividade' : 'Item'}
            name="evaluation_id"
            rules={[
              {
                required: true,
                message: 'Selecione o item'
              }
            ]}
          >
            {evaluationType === 'Assessment' ? (
              <Select
                {...assessmentSelectProps}
                notFoundContent={
                  <Empty description="Não há avaliações cadastradas para essa disciplina" />
                }
                placeholder="Selecione"
                disabled={!evaluationType}
                allowClear
              />
            ) : evaluationType === 'Assignment' ? (
              <Select
                {...assignmentSelectProps}
                notFoundContent={
                  <Empty description="Não há atividades cadastradas para essa disciplina" />
                }
                placeholder="Selecione"
                disabled={!evaluationType}
                allowClear
              />
            ) : (
              <Select disabled placeholder="Selecione o tipo primeiro" />
            )}
          </Form.Item>
        </Col>
      </Row>

      <Form.Item
        label="Nome da avaliação"
        name="name"
        rules={[
          {
            required: true,
            message: 'Por favor, insira o nome da avaliação'
          },
          { min: 3, message: 'O nome deve ter pelo menos 3 caracteres' }
        ]}
      >
        <Input placeholder="Digite o nome" />
      </Form.Item>
    </>
  );
};

export const ModalCreateEvaluationStep1 = ({
  assessmentSelectProps,
  allowAssignmentType,
  assignmentSelectProps,
  isReplacement,
  replacementParent
}: ModalCreateEvaluationStep1Props) => {
  if (allowAssignmentType && assignmentSelectProps) {
    return (
      <Step1WithAssignmentType
        assessmentSelectProps={assessmentSelectProps}
        assignmentSelectProps={assignmentSelectProps}
      />
    );
  }

  return (
    <Step1AssessmentOnly
      assessmentSelectProps={assessmentSelectProps}
      assignmentSelectProps={assignmentSelectProps}
      isReplacement={isReplacement}
      replacementParent={replacementParent}
    />
  );
};
